/**
 * Shared per-pod price calculation used by PDP (CartFlow) and PLP (NativeProductCard).
 *
 * Mirrors PDP logic: displayPrice / packageQuantity, formatted as currency.
 * The caller is responsible for passing the correct displayPrice based on
 * the current purchase type (subscription vs one-time).
 */

export interface PerPodInput {
  displayPriceCents: number;
  packageQuantity: number;
  productType?: string;
  isBulk?: boolean;
}

/**
 * Returns per-pod price in cents, or null if not applicable.
 */
export function getPerPodCents(input: PerPodInput): number | null {
  const { displayPriceCents, packageQuantity, productType, isBulk } = input;
  if (packageQuantity <= 0 || displayPriceCents <= 0) return null;
  if (productType === "Bundle") return null;
  if (isBulk) return null;
  return displayPriceCents / packageQuantity;
}

/**
 * Returns formatted per-pod string like "$0.39/pod", or null if not applicable.
 */
export function formatPerPod(input: PerPodInput): string | null {
  const cents = getPerPodCents(input);
  if (cents === null) return null;
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}/pod`;
}

/**
 * Returns formatted per-pod string wrapped in parentheses like "($0.39/pod)",
 * or empty string if not applicable.
 */
export function formatPerPodDisplay(input: PerPodInput): string {
  const formatted = formatPerPod(input);
  return formatted ? `(${formatted})` : "";
}
